import { memo } from 'react';
import styled from '@emotion/styled';
import { Edit as EditIcon } from '@mui/icons-material';

type ChatConfigReadOnlyProps = {
  initialMessage: string;
  questions: string[];
  onEdit: () => void;
  whitelabelEnabled?: boolean;
  primaryColor?: string;
};

const ChatConfigReadOnlyComponent = ({
  initialMessage,
  questions,
  onEdit,
  whitelabelEnabled,
  primaryColor,
}: ChatConfigReadOnlyProps) => {
  const iconColor = whitelabelEnabled && primaryColor ? primaryColor : 'var(--pri-02)';

  return (
    <>
      <Header>
        <Title>Customize Chat Questions</Title>
        <Description>
          Customize the initial message and starter questions shown in the chat interface for this segment&apos;s Digital Twin.
        </Description>
      </Header>

      <Field>
        <FieldLabel>Initial Message</FieldLabel>
        <DisplayContainer>
          <MessageDisplay>
            {initialMessage}
          </MessageDisplay>
          <IconButton
            iconColor={iconColor}
            onClick={onEdit}
            role="button"
            tabIndex={0}
            aria-label="Edit chat configuration"
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onEdit(); }}
          >
            <EditIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </DisplayContainer>
      </Field>

      <Field>
        <FieldLabel>Questions</FieldLabel>
        {questions.length > 0 ? (
          <QuestionsList>
            {questions.map((q, i) => (
              <QuestionItem key={i}>{q}</QuestionItem>
            ))}
          </QuestionsList>
        ) : (
          <Description>No questions configured.</Description>
        )}
      </Field>
    </>
  );
};

export const ChatConfigReadOnly = memo(ChatConfigReadOnlyComponent);

const Header = styled.div`
  margin-bottom: 16px;
`;

const Title = styled.h2`
  font-size: 18px;
  font-weight: 700;
  margin: 0 0 8px 0;
  color: var(--gray-d);
`;

const Description = styled.p`
  font-size: 14px;
  line-height: 1.6;
  color: var(--gray-m);
  margin: 0;
`;

type IconButtonProps = {
  iconColor: string;
};

const DisplayContainer = styled.div`
  display: flex;
  gap: 8px;
  align-items: flex-start;
`;

const IconButton = styled.div<IconButtonProps>`
  border-radius: 50%;
  border: 2px solid transparent;
  padding: 5px;
  transition: all 0.2s ease-in-out;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  flex-shrink: 0;

  svg {
    color: ${props => props.iconColor};
    fill: ${props => props.iconColor};
    transition: all 0.2s ease-in-out;
  }

  &:hover {
    border-color: ${props => props.iconColor};
    background-color: ${props => props.iconColor};

    svg {
      color: var(--pri-01);
      fill: var(--pri-01);
    }
  }
`;

const Field = styled.div`
  margin-top: 20px;
`;

const FieldLabel = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 700;
  margin-bottom: 12px;
  color: var(--gray-d);
`;

const MessageDisplay = styled.div`
  flex: 1;
  background-color: var(--gray-xl);
  border: 1px solid var(--gray-l);
  border-radius: 4px;
  padding: 16px;
  min-height: 60px;
  font-size: 14px;
  line-height: 1.6;
  color: var(--gray-d);
`;

const QuestionsList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const QuestionItem = styled.li`
  font-size: 14px;
  line-height: 1.6;
  color: var(--gray-d);
`;
